import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { BaseService } from '../../../@core/services/base.service';
import { environment } from '../../../../environments/environment.dev';
import { IUser, ResponseJson } from '../../../@core/models/model';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Runtime enum-like constant for prompt access scopes.
 * Use this instead of raw string literals so refactors stay safe.
 * ALL is a UI-only value that omits the accessLevel filter (shows personal + team).
 */
export const PromptAccessLevelEnum = {
    PERSONAL: 'PERSONAL',
    WORKSPACE: 'WORKSPACE',
    /** UI-only: no accessLevel filter sent to the backend — shows all prompts. */
    ALL: 'ALL',
} as const;

/** Access scope for a prompt. */
export type PromptAccessLevel = typeof PromptAccessLevelEnum[keyof typeof PromptAccessLevelEnum];

/** Embedding lifecycle state. */
export type PromptEmbeddingStatus = 'PENDING' | 'READY' | 'FAILED';

/** A prompt group label. */
export interface IPromptGroup {
    _id: string;
    name: string;
    organization: string;
    workspace: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    /** Number of prompts belonging to this group — computed by the list endpoint. */
    promptCount?: number;
}

/** A saved prompt enriched with access level and edit flag. */
export interface IPromptItem {
    _id: string;
    title: string;
    content: string;
    /** IDs of the groups this prompt belongs to. Empty array when ungrouped. */
    groups: string[];
    timesUsed: number;
    lastUsedAt: string | null;
    accessLevel: PromptAccessLevel;
    canEdit: boolean;
    embeddingStatus: PromptEmbeddingStatus;
    /** Author of the prompt; populated by the API when available. */
    createdBy?: Partial<IUser>;
    createdAt: string;
    updatedAt: string;
}

/** Shape returned by the list endpoint. */
export interface IPromptListResponse {
    prompts: IPromptItem[];
    total: {
        personal: number;
        team: number;
    }
}

/** Payload for creating a new prompt. */
export interface ICreatePromptPayload {
    title: string;
    content: string;
    groupIds?: string[];
    accessLevel: PromptAccessLevel;
    /** ID of the thread metadata document — set when saving an enhanced prompt so the
     *  backend can write the new prompt's _id back onto the LangGraph checkpoint message. */
    threadMetadataId?: string;
    /** ID of the human message that carries the approved_enhancement — set alongside
     *  threadMetadataId so the checkpoint update is targeted to the right message. */
    messageId?: string;
}

/** Payload for updating a prompt (all fields optional). */
export interface IUpdatePromptPayload {
    title?: string;
    content?: string;
    groupIds?: string[];
    accessLevel?: PromptAccessLevel;
}

/** Payload for listing prompts with optional filters. */
export interface IListPromptsPayload {
    groupId?: string;
    accessLevel?: PromptAccessLevel;
    search?: string;
    skip?: number;
    limit?: number;
    /** Field to sort by — passed directly to the backend. */
    sortField?: 'title' | 'timesUsed' | 'updatedAt' | 'createdAt' | 'lastUsedAt';
    /** Sort direction. */
    sortOrder?: 'asc' | 'desc';
}

/** Sort field options supported by the groups list endpoint. */
export type GroupSortField = 'recent' | 'name' | 'size' | 'updatedAt';

/** Payload for the paginated, searchable groups list endpoint. */
export interface IListGroupsPayload {
    /** Number of records to skip (for offset pagination). */
    skip?: number;
    /** Maximum number of groups to return. Omit to fetch all. */
    limit?: number;
    /** Case-insensitive name search filter. */
    search?: string;
    /** Field to sort by. Defaults to 'recent' (creation order). */
    sort?: GroupSortField;
    /** Sort direction. Defaults to 'desc'. */
    order?: 'asc' | 'desc';
}

/** Paginated groups response returned by the list endpoint. */
export interface IGroupListResponse {
    groups: IPromptGroup[];
    /** Total number of groups matching the current filters (before limit is applied). */
    total: number;
}

/**
 * At-a-glance summary — counts only.
 * Top prompts are fetched separately via listPrompts({ sortField: 'timesUsed', limit: 5 }).
 * Recent groups are fetched separately via listGroupsPaged({ sort: 'updatedAt', limit: 5 }).
 */
export interface IAtAGlanceData {
    /** Total personal prompts for the current user. */
    personalCount: number;
    /** Total team/workspace prompts. */
    teamCount: number;
    /** Total groups visible to the user. */
    groupCount: number;
    /** Σ timesUsed across all prompts visible to this user. */
    totalRuns: number;
}

/** A nudge suggestion item. */
export interface IPromptSuggestion {
    promptId: string;
    title: string;
    score: number;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * PromptLibraryService
 *
 * Angular HTTP service for Prompt Library CRUD, group management, and the
 * soft-nudge suggest endpoint.  All methods return Promises so they work
 * seamlessly with TanStack Query.
 */
@Injectable({ providedIn: 'root' })
export class PromptLibraryService extends BaseService {
    /** Base URL for all Prompt Library API calls. */
    private readonly apiUrl = environment.apiUrl + '/prompt-library';

    constructor(protected override http: HttpClient) {
        super(http);
    }

    // -----------------------------------------------------------------------
    // Groups
    // -----------------------------------------------------------------------

    /**
     * Fetch all groups for the current user (no pagination).
     * Used for editor form dropdowns, color-mapping helpers, etc.
     */
    listGroups(): Promise<ResponseJson<IPromptGroup[]>> {
        return lastValueFrom(this.get<IPromptGroup[]>(`${this.apiUrl}/groups`));
    }

    /**
     * Fetch a paginated, filtered, and sorted slice of groups.
     * Used by the groups grid on the library home screen.
     * Sends skip, limit, search, sort, and order in the POST body.
     */
    listGroupsPaged(payload: IListGroupsPayload = {}): Promise<ResponseJson<IGroupListResponse>> {
        return lastValueFrom(this.post<IGroupListResponse>(`${this.apiUrl}/groups/list`, payload));
    }

    /**
     * Fetch at-a-glance counts (personalCount, teamCount, groupCount, totalRuns).
     * Intentionally lightweight — no prompts or group lists included.
     */
    getAtAGlance(): Promise<ResponseJson<IAtAGlanceData>> {
        return lastValueFrom(this.get<IAtAGlanceData>(`${this.apiUrl}/at-a-glance`));
    }

    /**
     * Fetch the top 5 most-used prompts for the Top Prompts section.
     * Sorted by timesUsed desc with createdAt desc as a tiebreaker.
     */
    listTopPrompts(): Promise<ResponseJson<{ prompts: IPromptItem[]; total: { personal: number; team: number } }>> {
        return lastValueFrom(
            this.post<{ prompts: IPromptItem[]; total: { personal: number; team: number } }>(
                `${this.apiUrl}/prompts`,
                { sortField: 'timesUsed', sortOrder: 'desc', limit: 5, skip: 0 }
            )
        );
    }

    /** Create a new group. */
    createGroup(name: string): Promise<ResponseJson<IPromptGroup>> {
        return lastValueFrom(this.post<IPromptGroup>(`${this.apiUrl}/groups`, { name }));
    }

    /** Rename an existing group. */
    renameGroup(groupId: string, name: string): Promise<ResponseJson<IPromptGroup>> {
        return lastValueFrom(this.put<IPromptGroup>(`${this.apiUrl}/groups/${groupId}`, { name }));
    }

    /** Delete a group (prompts are detached, not deleted). */
    deleteGroup(groupId: string): Promise<ResponseJson<null>> {
        return lastValueFrom(this.delete<null>(`${this.apiUrl}/groups/${groupId}`));
    }

    // -----------------------------------------------------------------------
    // Prompts
    // -----------------------------------------------------------------------

    /** List prompts with optional filters. */
    listPrompts(payload: IListPromptsPayload = {}): Promise<ResponseJson<IPromptListResponse>> {
        return lastValueFrom(this.post<IPromptListResponse>(`${this.apiUrl}/prompts`, payload));
    }

    /** Fetch a single prompt by ID. */
    getPrompt(promptId: string): Promise<ResponseJson<IPromptItem>> {
        return lastValueFrom(this.get<IPromptItem>(`${this.apiUrl}/prompts/${promptId}`));
    }

    /** Create a new prompt. */
    createPrompt(payload: ICreatePromptPayload): Promise<ResponseJson<IPromptItem>> {
        return lastValueFrom(this.post<IPromptItem>(`${this.apiUrl}/prompts/create`, payload));
    }

    /** Update an existing prompt. */
    updatePrompt(promptId: string, payload: IUpdatePromptPayload): Promise<ResponseJson<IPromptItem>> {
        return lastValueFrom(this.put<IPromptItem>(`${this.apiUrl}/prompts/${promptId}`, payload));
    }

    /** Delete a prompt. */
    deletePrompt(promptId: string): Promise<ResponseJson<null>> {
        return lastValueFrom(this.delete<null>(`${this.apiUrl}/prompts/${promptId}`));
    }

    /**
     * Record usage and retrieve the prompt's content.
     * Called when "Run" is clicked — returns content for pre-filling the composer.
     */
    usePrompt(promptId: string): Promise<ResponseJson<{ content: string; title: string }>> {
        return lastValueFrom(this.post<{ content: string; title: string }>(
            `${this.apiUrl}/prompts/${promptId}/use`, {}
        ));
    }

    /** Find prompts semantically similar to a query string (soft-nudge). */
    suggestPrompts(query: string, limit = 3): Promise<ResponseJson<IPromptSuggestion[]>> {
        return lastValueFrom(this.post<IPromptSuggestion[]>(`${this.apiUrl}/prompts/suggest`, { query, limit }));
    }
}
